import Layer from "../Layer";
import SVGText from "./SVGText";
import { FontLoader, ImageLoader, ITextFormatMapper } from "../../helpers";
import { TextLayerProps, TextProps } from "./interfaces";
import { LayerCustomType, ObjectFitType, TextCaseType } from "../../interfaces";
import { FabricIText, FabricPattern } from "../../objects";
import { fitObjectPattern, pickProps, toTitleCase } from "../../utils";

export default class TextLayer extends Layer {
  protected static customType: LayerCustomType = LayerCustomType.Text;
  protected static defaultTypeProps: Partial<TextProps> = {
    fill: "#000000",
    fontFamily: "Times New Roman",
    maxFontSize: 200,
    paintFirst: "stroke",
    stroke: "#000000",
    strokeWidth: 0,
    strokeLineJoin: "round",
    textAlign: "center",
    prefix: "",
    infix: "A",
    suffix: "",
    textCase: TextCaseType.None,
    textColorsByLetter: false,
    letterColors: [],
  };
  protected textObject: FabricIText | SVGText;
  protected patternObject?: FabricPattern;

  name: string = "Text Layer";

  constructor(options: Partial<TextLayerProps> = {}) {
    const {
      transformSettings = {},
      typeSettings = {},
      ...layerProps
    } = options;
    super();

    const textOptions = { ...TextLayer.defaultTypeProps, ...typeSettings };
    this.textObject =
      typeSettings.class === "i-text"
        ? new FabricIText("", textOptions)
        : new SVGText("", textOptions);
    this.insertAt(0, this.textObject);

    this.set(transformSettings);
    this.set(layerProps);

    this.loadFont(typeSettings.fontSrc);
    this.loadPattern(
      typeSettings.patternImageSrc || (typeSettings.fill as string),
    );
  }

  protected rerender() {
    const fontSizeScalable = this.textObject.fontSizeScalable;
    const fontSizeScale = fontSizeScalable
      ? Math.min(this.scaleX, this.scaleY)
      : 1;
    super.rerender();
    this.updateText();
    this.updateFontSize(fontSizeScale);
    this.updatePosition();
    this.updatePattern();
    this.canvas?.requestRenderAll();
  }

  toObject() {
    const object = super.toObject();
    const textProps = this.getTextProps();
    return { ...object, typeSettings: textProps };
  }

  protected updateText() {
    const {
      prefix = "",
      infix = "",
      suffix = "",
      textCase = "",
    } = this.textObject;

    let text = infix ? `${prefix}${infix}${suffix}` : "";
    if (textCase === "lowercase") {
      text = text.toLowerCase();
    } else if (textCase === "title-case") {
      text = toTitleCase(text);
    } else if (textCase === "uppercase") {
      text = text.toUpperCase();
    }

    this.textObject.set({ text });
    if (this.textObject instanceof FabricIText) {
      ITextFormatMapper(this.textObject, text);
    }
    this.canvas?.requestRenderAll();
  }

  protected updateFontSize(scale = 1, deltaFontSize = 0.1) {
    this.textObject.maxFontSize *= scale;
    this.textObject.strokeWidth *= scale;
    this.textObject.charSpacing *= scale;

    const layerWidth = this.width;
    const layerHeight = this.height;

    let low = 0;
    let high = this.textObject.maxFontSize;
    let bestFit = this.textObject.fontSize;

    const padding = this.textObject.strokeWidth;

    while (high - low > deltaFontSize) {
      const mid = (low + high) / 2;
      this.textObject.set({ fontSize: mid });
      const textWidth = this.getTextWidth() + padding;
      const textHeight = this.getTextHeight() + padding;
      if (textWidth <= layerWidth && textHeight <= layerHeight) {
        bestFit = mid;
        low = mid;
      } else {
        high = mid;
      }
    }

    this.textObject.set({ fontSize: bestFit });
    this.canvas?.requestRenderAll();
  }

  protected updatePosition() {
    const {
      textAlign,
      width: textWidth,
      height: textHeight,
      strokeWidth,
    } = this.textObject;

    const getLeft = (align: string) => {
      switch (align) {
        case "left":
          return -this.width / 2;
        case "center":
          return -textWidth / 2 - strokeWidth / 2;
        case "right":
          return this.width / 2 - textWidth - strokeWidth;
      }
    };

    this.textObject.set({
      left: getLeft(textAlign),
      top: -textHeight / 2 - strokeWidth / 2,
    });

    this.canvas?.requestRenderAll();
  }

  protected updatePattern() {
    if (!this.patternObject) return;

    const patternSrc = this.patternObject.source as HTMLImageElement;
    if (!patternSrc) return;

    const patternScale = this.textObject.patternScale;
    const patternFit = this.textObject.patternFit ?? "line";

    if (patternFit === "cover") {
      fitObjectPattern(this.patternObject, this, ObjectFitType.Cover, patternScale);
      return;
    }

    const { height: imgHeight } = patternSrc;
    if (!imgHeight) return;

    const { fontSize, lineHeight } = this.textObject;
    const actualLineHeight = fontSize * (lineHeight || 1);
    const scale = (actualLineHeight / imgHeight) * patternScale;
    this.patternObject.patternTransform = [scale, 0, 0, scale, 0, 0];
  }

  setTextProps(props: Partial<TextProps>) {
    this.textObject.set(props);
    this.rerender();
  }

  getTextProps(key?: string) {
    if (key) {
      return this.textObject.get(key);
    } else {
      const textProps = pickProps(
        this.textObject as unknown as TextProps,
        [
          "prefix",
          "infix",
          "suffix",
          "fill",
          "fontPath",
          "fontSrc",
          "fontFamily",
          "fontSize",
          "maxFontSize",
          "textAlign",
          "textCase",
          "textDirection",
          "charSpacing",
          "lineHeight",
          "stroke",
          "strokeWidth",
          "globalCompositeOperation",
          "textColorsByLetter",
          "letterColors",
          "patternImagePath",
          "patternScale",
          "patternFit",
        ],
      ) as Partial<TextProps>;

      if (textProps.fill instanceof FabricPattern) {
        textProps.fill = TextLayer.defaultTypeProps.fill || "";
      }

      textProps.class =
        this.textObject instanceof SVGText ? "svg-text" : "i-text";

      return textProps;
    }
  }

  protected getTextWidth(): number {
    if (this.textObject instanceof SVGText) {
      return this.textObject.width;
    } else {
      return Math.max(...(this.textObject as FabricIText).__lineWidths);
    }
  }

  protected getTextHeight(): number {
    return this.textObject.height;
  }

  async loadFont(fontSrc?: string) {
    this.textObject.set({ fontSrc });

    const fontFace = fontSrc
      ? await FontLoader.loadFontFace(fontSrc)
      : undefined;
    this.textObject.set({
      fontFamily: fontFace?.family || TextLayer.defaultTypeProps.fontFamily,
    });

    if (this.textObject instanceof SVGText) {
      await this.textObject.loadFontFromUrl(fontSrc);
    }

    this.rerender();
  }

  async loadPattern(src?: string | Blob) {
    if (!src) {
      this.textObject.set({ fill: TextLayer.defaultTypeProps.fill });
      return;
    }

    const srcToLoad = src instanceof Blob ? URL.createObjectURL(src) : src;

    if (srcToLoad.startsWith("#")) {
      this.textObject.set({ fill: srcToLoad });
      return;
    }

    try {
      const imageElement = await ImageLoader.load(srcToLoad);
      if (!imageElement) return "";

      if (this.patternObject) {
        this.patternObject.source = imageElement;
      } else {
        this.patternObject = new FabricPattern({
          source: imageElement,
          crossOrigin: "anonymous",
          repeat: "repeat",
        });
      }

      this.textObject.set({ fill: this.patternObject });
      this.rerender();
      return srcToLoad;
    } catch (error) {
      console.error(error);
      return "";
    }
  }
}

export * from "./interfaces";
